import { useState } from "react";
import { useParams } from "react-router-dom";
import { Icon } from "../components/Icon";
import { sinceLastSeenEventCopy } from "../components/SinceLastSeenSummary";
import { browserSessionStorage, MANAGER_DRAFT_MAX_LENGTH, managerDraftStorageKey, readManagerDraft, writeManagerDraft } from "../lib/crewContinuity";
import { formatTime } from "../lib/time";
import { useRemote } from "../state/RemoteContext";

export function managerTranscriptEmptyCopy(status: "available" | "unavailable") {
  return status === "unavailable"
    ? "Manager transcript unavailable."
    : "No Manager messages yet.";
}

export function managerTranscriptHistoryCopy(historyTruncated: boolean) {
  return historyTruncated ? "Earlier Manager history is not included in this session." : null;
}

export function ManagerScreen() {
  const { crewId } = useParams();
  const { fixture, canChat, managerBlockReason, actions, setDialog } = useRemote();
  const crew = fixture.crews.find((candidate) => candidate.id === crewId);
  const resolvedCrewId = crewId ?? "";
  const draftKey = managerDraftStorageKey(fixture.session.sessionId, resolvedCrewId);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const draft = Object.prototype.hasOwnProperty.call(drafts, draftKey)
    ? drafts[draftKey]
    : readManagerDraft(browserSessionStorage(), fixture.session.sessionId, resolvedCrewId);
  const setDraft = (value: string) => {
    writeManagerDraft(browserSessionStorage(), fixture.session.sessionId, resolvedCrewId, value);
    setDrafts((current) => ({ ...current, [draftKey]: value }));
  };
  const [voiceNotice, setVoiceNotice] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [lastOperationId, setLastOperationId] = useState<string | null>(null);

  if (!crew) return <div className="screen empty-state"><h1>Manager unavailable</h1></div>;
  const send = async () => {
    const text = draft.trim();
    if (!text || !canChat) return;
    setSendError(null);
    const operationId = `op-${crypto.randomUUID()}`;
    const messageId = `msg-${crypto.randomUUID()}`;
    setLastOperationId(operationId);
    try {
      const receipt = await actions.sendMessage.mutateAsync({ operationId, messageId, crewId: crew.id, text });
      if (["requested", "forwarded", "authority-accepted", "effective"].includes(receipt.state)) setDraft("");
      else setSendError(receipt.detail ?? `The Manager message is ${receipt.state}. Your draft was retained.`);
    } catch (error) {
      setSendError(error instanceof Error ? error.message : "The Manager message was rejected.");
    }
  };

  const dictate = () => {
    setVoiceNotice("Use the phone keyboard’s dictation control. Voice always lands in this draft and never sends automatically.");
    document.querySelector<HTMLTextAreaElement>("#manager-draft")?.focus();
  };

  const openReference = (reference: string) => {
    const decision = crew.decisions.find((candidate) => candidate.id === reference);
    setDialog(decision
      ? { type: "decision", crewId: crew.id, decisionId: decision.id }
      : { type: "board", crewId: crew.id, itemId: reference });
  };

  return (
    <div className="manager-layout">
      <div className="screen manager-screen">
        <header className="manager-status">
          <span><strong>{crew.runState}</strong>{crew.since.countsAvailable && crew.since.decisionCount ? ` · ${crew.since.decisionCount} decision` : ""}</span>
          <p>{crew.summary}</p>
        </header>
        <div className="context-divider" data-counts-available={crew.since.countsAvailable}><span>Since {formatTime(crew.since.from)} · {sinceLastSeenEventCopy(crew.since)}</span></div>
        <section className="transcript" aria-label={`${crew.name} Manager conversation`}>
          {crew.manager.historyTruncated && (
            <div className="context-divider transcript-history-marker" role="note">
              <span>{managerTranscriptHistoryCopy(true)}</span>
            </div>
          )}
          {crew.manager.messages.length === 0 && <div className="empty-state"><p>{managerTranscriptEmptyCopy(crew.manager.status)}</p></div>}
          {crew.manager.messages.map((message) => (
            <article className="message" data-author={message.authorType} key={message.id}>
              <div className="message-avatar" aria-hidden="true">{message.authorType === "manager" ? "CM" : message.authorType === "operator" ? "YOU" : "MB"}</div>
              <div className="message-body">
                <header><strong>{message.authorLabel}</strong><time dateTime={message.createdAt}>{formatTime(message.createdAt)}</time></header>
                <p>{message.text}</p>
                {message.reference && <button type="button" className="message-reference" onClick={() => openReference(message.reference!)}>{message.reference}</button>}
                {message.delivery && <small className="delivery">{message.delivery}</small>}
              </div>
            </article>
          ))}
        </section>
        <div className="composer">
          {voiceNotice && <p className="composer-notice" role="status">{voiceNotice}</p>}
          {!canChat && <p className="permission-note">Manager chat is read-only. {managerBlockReason}</p>}
          {lastOperationId && actions.operations[lastOperationId] && <p className="operation-banner" data-state={actions.operations[lastOperationId].state} role="status">manager.chat · {actions.operations[lastOperationId].state}</p>}
          {sendError && <p className="error-copy" role="alert">{sendError}</p>}
          <div className="composer-row">
            <button type="button" className="icon-button mic-button" onClick={dictate} disabled={!canChat} aria-label="Compose with phone dictation"><Icon name="mic" /></button>
            <label className="visually-hidden" htmlFor="manager-draft">Message the Crew Manager</label>
            <textarea id="manager-draft" rows={1} value={draft} maxLength={MANAGER_DRAFT_MAX_LENGTH} disabled={!canChat} onChange={(event) => setDraft(event.target.value)} placeholder={canChat ? "Message the Crew Manager…" : "Manager chat is not granted"} />
            <button type="button" className="send-button" onClick={send} disabled={!canChat || !draft.trim() || actions.sendMessage.isPending} aria-label="Send message"><Icon name="chevron" /></button>
          </div>
        </div>
      </div>
      <aside className="workboard-rail" aria-label="Live Workboard">
        <h2>Live Workboard</h2>
        {crew.board.slice(0, 4).map((item) => (
          <button type="button" key={item.id} data-status={item.status} onClick={() => setDialog({ type: "board", crewId: crew.id, itemId: item.id })}>
            <small>{item.id} · {item.priority}</small><strong>{item.title}</strong>
          </button>
        ))}
      </aside>
    </div>
  );
}
